/**
 * Human-readable labels + previews for multiplayer / session-action tools
 * that require approval (share, invite, email, delete, codes, team config).
 * Kept out of ApprovalRequestBlock.display.tsx to stay under max-lines.
 */
import { defineMessages, type IntlShape } from 'react-intl';

export const EMAIL_SESSION_CONTENT_TOOL_NAME = 'email_session_content';

// Keys are the tool names — `MULTIPLAYER_APPROVAL_TOOL_NAMES` derives from them.
const MULTIPLAYER_APPROVAL_LABELS = defineMessages({
  share_session: {
    defaultMessage: 'Share session',
    id: 'OLYrb61Z10',
    description: 'Approval card title: share a session with colleagues',
  },
  unshare_session: {
    defaultMessage: 'Revoke session access',
    id: 'pFf1UmR0Mx',
    description: 'Approval card title: revoke a colleague’s access to a session',
  },
  invite_team_member: {
    defaultMessage: 'Invite team member',
    id: 'zFrMNUf2Gw',
    description: 'Approval card title: invite a colleague to the team',
  },
  remove_team_member: {
    defaultMessage: 'Remove team member',
    id: '4CJahHnFjg',
    description: 'Approval card title: remove a colleague from the team',
  },
  share_template: {
    defaultMessage: 'Share template',
    id: 'RrHBQQcjwC',
    description: 'Approval card title: share a team template',
  },
  unshare_template: {
    defaultMessage: 'Stop sharing template',
    id: 'WRielflc/t',
    description: 'Approval card title: stop sharing a team template',
  },
  // Session / template action tools (ml-scribe agent session-action-tools).
  email_session_content: {
    defaultMessage: 'Email session content',
    id: '6uBOAxfqJD',
    description: 'Approval card title: email session content to recipients',
  },
  delete_session: {
    defaultMessage: 'Delete session',
    id: 'SE17E1d4l8',
    description: 'Approval card title: delete a session',
  },
  delete_template: {
    defaultMessage: 'Delete template',
    id: 'JpiYdbPD4F',
    description: 'Approval card title: delete a template',
  },
  create_session_for_member: {
    defaultMessage: 'Create session for teammate',
    id: 'NJUcNyvOxF',
    description: 'Approval card title: create a session owned by a teammate',
  },
  confirm_session_codes: {
    defaultMessage: 'Confirm clinical codes',
    id: 'PXeNrcinzU',
    description: 'Approval card title: confirm suggested clinical codes',
  },
  reject_session_code: {
    defaultMessage: 'Reject clinical code',
    id: 'yMyDS0yUha',
    description: 'Approval card title: reject a suggested clinical code',
  },
  add_session_code: {
    defaultMessage: 'Add clinical code',
    id: 'S94mfl5B3Z',
    description: 'Approval card title: add a clinical code to a session',
  },
  update_team_session_configuration: {
    defaultMessage: 'Update team session sharing',
    id: 'DSBQFACw0+',
    description: 'Approval card title: change team session sharing configuration',
  },
});

const PREVIEW_MESSAGES = defineMessages({
  shareSessionViewWithCount: {
    defaultMessage:
      'Grant view access to {count, plural, one {# colleague} other {# colleagues}}.',
    id: 'sxfLBiFt7D',
    description: 'Approval preview: share a session with view-only access',
  },
  shareSessionEditWithCount: {
    defaultMessage:
      'Grant edit access to {count, plural, one {# colleague} other {# colleagues}}.',
    id: 'SgY0gBw0J8',
    description: 'Approval preview: share a session with edit access',
  },
  shareSessionFullAccessWithCount: {
    defaultMessage:
      'Grant full access to {count, plural, one {# colleague} other {# colleagues}}.',
    id: 'debaTZtrrJ',
    description: 'Approval preview: share a session with full access',
  },
  // Raw-level disclosure for a ShareLevel this build does not know yet: the
  // consent prompt must still show the scope being granted, so the backend's
  // level token is interpolated verbatim. Ids are the original interpolating
  // messages' — moved verbatim, so their translations carry over.
  shareSessionRawLevelWithCount: {
    defaultMessage:
      'Grant {level} access to {count, plural, one {# colleague} other {# colleagues}}.',
    id: 'D6O0PXdExX',
    description: 'Approval preview for sharing a session with colleagues',
  },
  shareSessionRawLevel: {
    defaultMessage: 'Grant {level} access.',
    id: 'hhGespJho9',
    description:
      'Approval preview: share a session; {level} is the access level, e.g. “view”',
  },
  shareSessionWithCount: {
    defaultMessage:
      'Grant access to {count, plural, one {# colleague} other {# colleagues}}.',
    id: '/6YI3ZwZU3',
    description: 'Approval preview: share a session; access level not recognised',
  },
  shareSessionView: {
    defaultMessage: 'Grant view access.',
    id: 'l2tqRfrtCq',
    description: 'Approval preview: share a session with view-only access',
  },
  shareSessionEdit: {
    defaultMessage: 'Grant edit access.',
    id: 'WfqVhtvm6k',
    description: 'Approval preview: share a session with edit access',
  },
  shareSessionFullAccess: {
    defaultMessage: 'Grant full access.',
    id: 'glEwxZB+R5',
    description: 'Approval preview: share a session with full access',
  },
  shareSession: {
    defaultMessage: 'Grant access.',
    id: 'QE+KTKyvJ6',
    description: 'Approval preview: share a session; access level not recognised',
  },
  unshareSession: {
    defaultMessage: 'Revoke a colleague’s direct access to this session.',
    id: 'SJaJDCd4yH',
    description: 'Approval preview: revoke session access',
  },
  inviteTeamMemberWithEmail: {
    defaultMessage: 'Invite {email} to the team.',
    id: 'BvbSpyb85S',
    description: 'Approval preview: invite a colleague by email address',
  },
  inviteTeamMember: {
    defaultMessage: 'Invite a colleague to the team.',
    id: 'FHEXpB13dh',
    description: 'Approval preview: invite a colleague',
  },
  removeTeamMember: {
    defaultMessage: 'Remove a colleague from the team.',
    id: 'Gd5S3fSvrR',
    description: 'Approval preview: remove a team member',
  },
  shareTemplate: {
    defaultMessage: 'Share a team template with other teams.',
    id: 'RUhN9zCqN2',
    description: 'Approval preview: share a template',
  },
  unshareTemplate: {
    defaultMessage: 'Stop sharing a team template with other teams.',
    id: 'qYLdP+/h22',
    description: 'Approval preview: stop sharing a template',
  },
  emailSessionDocument: {
    defaultMessage:
      'Email a session document (letter/referral) to {count, plural, =0 {recipients} one {# recipient} other {# recipients}}. Outbound email — confirm recipients.',
    id: '0xpRS68FXZ',
    description: 'Approval preview: email a session document to recipients',
  },
  emailConsultNote: {
    defaultMessage:
      'Email the consult note to {count, plural, =0 {recipients} one {# recipient} other {# recipients}}. Outbound email — confirm recipients.',
    id: 'EHvH0TQ7tb',
    description: 'Approval preview: email the consult note to recipients',
  },
  deleteSessionsWithCount: {
    defaultMessage:
      'Delete {count, plural, one {# session} other {# sessions}} (and linked clinical tasks).',
    id: 'I3IKTG/p6p',
    description: 'Approval preview for deleting one or more sessions',
  },
  deleteSessions: {
    // Deliberate rewording from the legacy 'session(s)' — kept plural on review.
    defaultMessage: 'Delete the selected sessions.',
    id: 'orD+PURtoT',
    description: 'Approval preview: delete sessions, count unknown',
  },
  deleteTemplate: {
    defaultMessage: 'Delete this template from the library.',
    id: 'E8ROYZ3s3A',
    description: 'Approval preview: delete a template',
  },
  createSessionForMember: {
    defaultMessage: 'Create a new session owned by a teammate.',
    id: 'wotiZjI+7y',
    description: 'Approval preview: create a session for a teammate',
  },
  confirmSessionCodes: {
    defaultMessage: 'Confirm suggested clinical codes on this session.',
    id: 'aI0k8eK1gT',
    description: 'Approval preview: confirm clinical codes',
  },
  rejectSessionCode: {
    defaultMessage: 'Reject a suggested clinical code on this session.',
    id: 'CqkVjTY1CU',
    description: 'Approval preview: reject a clinical code',
  },
  addSessionCode: {
    defaultMessage: 'Add a clinical code to this session.',
    id: 'cMCnOi9MR1',
    description: 'Approval preview: add a clinical code',
  },
  updateTeamSessionConfiguration: {
    defaultMessage:
      'Change who on the team can view others’ sessions (view-others allowlist).',
    id: '79o1MGlDsR',
    description: 'Approval preview: update team session sharing configuration',
  },
});

// Mirrors the `ShareLevel` enum in @heidi/schema/ml-scribe (kept local so this
// display table does not pull the generated schema module in as a value import;
// the suite pins the mirror to the enum with a total Record).
const SHARE_ACCESS_PREVIEWS = {
  view: {
    withCount: PREVIEW_MESSAGES.shareSessionViewWithCount,
    withoutCount: PREVIEW_MESSAGES.shareSessionView,
  },
  edit: {
    withCount: PREVIEW_MESSAGES.shareSessionEditWithCount,
    withoutCount: PREVIEW_MESSAGES.shareSessionEdit,
  },
  full_access: {
    withCount: PREVIEW_MESSAGES.shareSessionFullAccessWithCount,
    withoutCount: PREVIEW_MESSAGES.shareSessionFullAccess,
  },
} as const;

const formatShareSessionPreview = (
  intl: IntlShape,
  input: Record<string, unknown>
): string => {
  const count = Array.isArray(input.clinician_ids) ? input.clinician_ids.length : 0;
  const level = typeof input.level === 'string' ? input.level.trim() : '';
  // Object.hasOwn: level is backend-supplied, and a bare index would reach
  // Object.prototype members ('constructor', …) and crash formatMessage.
  if (level && Object.hasOwn(SHARE_ACCESS_PREVIEWS, level)) {
    const previews = SHARE_ACCESS_PREVIEWS[level as keyof typeof SHARE_ACCESS_PREVIEWS];
    return count > 0
      ? intl.formatMessage(previews.withCount, { count })
      : intl.formatMessage(previews.withoutCount);
  }
  if (level) {
    // A level this build does not know is still the scope of a PHI grant, so
    // the consent prompt discloses the raw token rather than approving blind.
    const rawLevel = level.split('_').join(' ');
    return count > 0
      ? intl.formatMessage(PREVIEW_MESSAGES.shareSessionRawLevelWithCount, {
          level: rawLevel,
          count,
        })
      : intl.formatMessage(PREVIEW_MESSAGES.shareSessionRawLevel, { level: rawLevel });
  }
  return count > 0
    ? intl.formatMessage(PREVIEW_MESSAGES.shareSessionWithCount, { count })
    : intl.formatMessage(PREVIEW_MESSAGES.shareSession);
};

const recipientCount = (input: Record<string, unknown>): number => {
  if (Array.isArray(input.to_emails)) return input.to_emails.length;
  if (typeof input.to_email === 'string' && input.to_email.trim()) return 1;
  return 0;
};

/** The tools opted in to a multiplayer approval body, for the approval-input registry. */
export const MULTIPLAYER_APPROVAL_TOOL_NAMES: readonly string[] = Object.keys(
  MULTIPLAYER_APPROVAL_LABELS
);

/** Human tool label for the card title / subtitle. Not a tool input. */
export const readMultiplayerApprovalLabel = (
  intl: IntlShape,
  toolName: string
): string | undefined => {
  // Object.hasOwn: tool_name is backend-supplied, and a bare index would reach
  // Object.prototype members ('constructor', …) and crash formatMessage.
  const message = Object.hasOwn(MULTIPLAYER_APPROVAL_LABELS, toolName)
    ? MULTIPLAYER_APPROVAL_LABELS[toolName as keyof typeof MULTIPLAYER_APPROVAL_LABELS]
    : undefined;
  return message ? intl.formatMessage(message) : undefined;
};

export const readMultiplayerApprovalPreview = (
  intl: IntlShape,
  toolName: string,
  input: Record<string, unknown>
): string | undefined => {
  switch (toolName) {
    case 'share_session':
      return formatShareSessionPreview(intl, input);
    case 'unshare_session':
      return intl.formatMessage(PREVIEW_MESSAGES.unshareSession);
    case 'invite_team_member': {
      const email = typeof input.email === 'string' ? input.email.trim() : '';
      return email
        ? intl.formatMessage(PREVIEW_MESSAGES.inviteTeamMemberWithEmail, { email })
        : intl.formatMessage(PREVIEW_MESSAGES.inviteTeamMember);
    }
    case 'remove_team_member':
      return intl.formatMessage(PREVIEW_MESSAGES.removeTeamMember);
    case 'share_template':
      return intl.formatMessage(PREVIEW_MESSAGES.shareTemplate);
    case 'unshare_template':
      return intl.formatMessage(PREVIEW_MESSAGES.unshareTemplate);
    case EMAIL_SESSION_CONTENT_TOOL_NAME: {
      const kind = typeof input.kind === 'string' ? input.kind : '';
      const count = recipientCount(input);
      return kind === 'document'
        ? intl.formatMessage(PREVIEW_MESSAGES.emailSessionDocument, { count })
        : intl.formatMessage(PREVIEW_MESSAGES.emailConsultNote, { count });
    }
    case 'delete_session': {
      const ids = Array.isArray(input.session_ids) ? input.session_ids : [];
      const count = ids.length;
      return count > 0
        ? intl.formatMessage(PREVIEW_MESSAGES.deleteSessionsWithCount, { count })
        : intl.formatMessage(PREVIEW_MESSAGES.deleteSessions);
    }
    case 'delete_template':
      return intl.formatMessage(PREVIEW_MESSAGES.deleteTemplate);
    case 'create_session_for_member':
      return intl.formatMessage(PREVIEW_MESSAGES.createSessionForMember);
    case 'confirm_session_codes':
      return intl.formatMessage(PREVIEW_MESSAGES.confirmSessionCodes);
    case 'reject_session_code':
      return intl.formatMessage(PREVIEW_MESSAGES.rejectSessionCode);
    case 'add_session_code':
      return intl.formatMessage(PREVIEW_MESSAGES.addSessionCode);
    case 'update_team_session_configuration':
      return intl.formatMessage(PREVIEW_MESSAGES.updateTeamSessionConfiguration);
    default:
      return undefined;
  }
};
